import { computed, inject, toValue, watch, type MaybeRefOrGetter, type Ref } from 'vue';
import { routerKey } from 'vue-router';

type HistoryMode = 'push' | 'replace';

export interface ModalQueryNameOptions {
  /**
   * The value written to the query when this modal is open (e.g. `recharge-balance`
   * for `?modal=recharge-balance`). Sync is inactive when omitted.
   */
  name: MaybeRefOrGetter<string | undefined>;
  /**
   * The URL query key to synchronize.
   * @default 'modal'
   */
  urlQueryKey?: MaybeRefOrGetter<string>;
  /**
   * Whether this instance owns the query key. Disabled instances never read or
   * write the URL, which is what keeps several instances mounted on the same
   * route — one per hidden tab, say — from fighting each other. Combine with
   * policy in the parent: `:url-query-sync-enabled="isActive && canOpen"`.
   * @default true
   */
  enabled?: MaybeRefOrGetter<boolean>;
  /** The boolean open model to keep in sync with the URL. */
  model: Ref<boolean | undefined>;
}

/**
 * Sync one modal's boolean `v-model` with a shared router query param.
 *
 * - Active only when {@link ModalQueryNameOptions.name} is set.
 * - No-ops when Vue Router is not installed (Storybook, non-routed embeds).
 * - Opening pushes a history entry; closing replaces it, so back closes once.
 * - Values that are not this modal's name are left alone (sticky until a
 *   claiming modal mounts).
 * - When disabled, the local model closes without touching the URL so another
 *   instance can take over.
 */
export const useModalQueryName = (options: ModalQueryNameOptions): void => {
  const router = inject(routerKey, null);
  if (!router) {
    return;
  }

  const key = computed(() => toValue(options.urlQueryKey) ?? 'modal');
  const name = computed(() => {
    const value = toValue(options.name);
    return value || undefined;
  });
  const enabled = computed(() => (options.enabled == null ? true : !!toValue(options.enabled)));
  const active = computed(() => !!name.value && enabled.value);

  const queryValue = (): string | undefined => {
    const value = router.currentRoute.value.query[key.value];
    return typeof value === 'string' ? value : undefined;
  };

  const matchesName = (): boolean => !!name.value && queryValue() === name.value;

  const navigate = (value: string | undefined, mode: HistoryMode): void => {
    if (queryValue() === value) {
      return;
    }

    const currentRoute = router.currentRoute.value;
    const query = { ...currentRoute.query };

    if (value == null) {
      delete query[key.value];
    } else {
      query[key.value] = value;
    }

    const target = { path: currentRoute.path, hash: currentRoute.hash, query };
    if (mode === 'push') {
      void router.push(target);
    } else {
      void router.replace(target);
    }
  };

  /** URL wins: open when it names us, otherwise close locally without stripping foreign values. */
  const syncFromQuery = (): void => {
    if (!name.value) {
      return;
    }

    const shouldOpen = matchesName();
    if (!!options.model.value !== shouldOpen) {
      options.model.value = shouldOpen;
    }
  };

  if (active.value) {
    if (options.model.value && queryValue() == null) {
      navigate(name.value!, 'replace');
    } else {
      syncFromQuery();
    }
  }

  watch(
    () => router.currentRoute.value.query[key.value],
    () => {
      if (active.value) {
        syncFromQuery();
      }
    },
  );

  watch(name, () => {
    if (active.value) {
      syncFromQuery();
    }
  });

  watch(enabled, (isEnabled) => {
    if (!name.value) {
      return;
    }

    if (isEnabled) {
      syncFromQuery();
      return;
    }

    // Hand the key over without touching the URL; the newly enabled instance adopts it.
    if (options.model.value) {
      options.model.value = false;
    }
  });

  watch(
    () => options.model.value,
    (isOpen) => {
      if (!active.value || !name.value) {
        return;
      }

      if (isOpen) {
        navigate(name.value, 'push');
        return;
      }

      if (matchesName()) {
        navigate(undefined, 'replace');
      }
    },
  );
};
